import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, limit, onSnapshot, Timestamp, type QueryConstraint, type DocumentData } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { SITES, DOC_CATEGORIES, isGlobal, canManageExpiry, canUploadNewVersion, type Member } from "../domain";
import { friendly, toast } from "../errors";

type Row = DocumentData & { id: string };
const DAY = 86400000;

/** Tài liệu có hạn hiệu lực, theo đúng phạm vi đọc của từng vai trò (khớp firestore.rules). */
function expiryQuery(m: Member) {
  const c: QueryConstraint[] = [where("expiresAt", ">", Timestamp.fromMillis(0)), orderBy("expiresAt", "asc"), limit(500)];
  if (isGlobal(m)) return query(collection(db, "documents"), ...c);
  const sites = [...m.sites, "AHG"];
  if (m.role === "site_mgr") return query(collection(db, "documents"), where("siteId", "in", sites), ...c);
  return query(collection(db, "documents"), where("staffVisible", "==", true), where("siteId", "in", sites), ...c);
}

const GROUPS = [
  { key: "over", label: "Đã quá hạn", cls: "g-over", test: (d: number) => d <= 0 },
  { key: "d7", label: "Trong 7 ngày", cls: "g-red", test: (d: number) => d > 0 && d <= 7 },
  { key: "d30", label: "Trong 30 ngày", cls: "g-gold", test: (d: number) => d > 7 && d <= 30 },
  { key: "d90", label: "Trong 90 ngày", cls: "g-soft", test: (d: number) => d > 30 && d <= 90 },
  { key: "later", label: "Sau 90 ngày", cls: "g-ok", test: (d: number) => d > 90 },
] as const;

const fmt = (t?: Timestamp) => t ? t.toDate().toLocaleDateString("vi-VN") : "—";
const toInput = (t?: Timestamp) => t ? new Date(t.toMillis() + 7 * 3600e3).toISOString().slice(0, 10) : "";

export default function Compliance() {
  const { member } = useSession();
  const nav = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [site, setSite] = useState("");
  const [cat, setCat] = useState("");
  const [editing, setEditing] = useState<{ id: string; mode: "date" | "renew"; value: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const scopeKey = member ? `${member.role}|${member.sites.join(",")}` : "";
  useEffect(() => {
    if (!member) return;
    return onSnapshot(expiryQuery(member), (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => setErr(friendly(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  const now = Date.now();
  const items = useMemo(() => rows
    .filter((r) => r.latestVersion > 0 && r.code && r.status !== "trashed" && (!site || r.siteId === site) && (!cat || r.category === cat))
    .map((r): Row & { daysLeft: number } => ({ ...r, daysLeft: Math.ceil((r.expiresAt.toMillis() - now) / DAY) })), [rows, site, cat, now]);

  if (!member) return null;
  const siteOpts = isGlobal(member) ? Object.keys(SITES) : [...member.sites, "AHG"];
  const catOpts = [...new Set(rows.filter((r) => r.latestVersion > 0).map((r) => r.category as string))].sort();

  const save = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      if (editing.mode === "date") await call("setDocumentExpiry")({ docId: editing.id, expiresAt: editing.value || null });
      else await call("setRenewal")({ docId: editing.id, state: "in_progress", note: editing.value });
      toast(editing.mode === "date" ? "Đã cập nhật ngày hết hạn." : "Đã đánh dấu đang gia hạn.", "ok");
      setEditing(null);
    } catch (e) { toast(friendly(e)); }
    setBusy(false);
  };
  const clearRenewal = async (id: string) => {
    try { await call("setRenewal")({ docId: id, state: "none" }); toast("Đã bỏ đánh dấu gia hạn.", "ok"); } catch (e) { toast(friendly(e)); }
  };
  const runNow = async () => {
    setBusy(true);
    try { const r = await call<object, { created: number }>("runLegalRemindersNow")({}); toast(`Đã tạo ${r.created} thông báo nhắc hạn.`, "ok"); }
    catch (e) { toast(friendly(e)); }
    setBusy(false);
  };

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div><h1>Lịch pháp lý</h1>
          <p className="sub">Hợp đồng, giấy phép, bảo hiểm… có ngày hiệu lực. Hub tự nhắc trước 90, 30, 7 ngày và khi quá hạn.</p></div>
        {member.role === "admin" && <button className="btn" disabled={busy} onClick={runNow}>Chạy nhắc hạn ngay</button>}
      </div>

      <div className="tiles">
        {GROUPS.slice(0, 4).map((g) => (
          <div key={g.key} className={`tile ${g.cls}`}><b>{items.filter((i) => g.test(i.daysLeft)).length}</b><span>{g.label}</span></div>
        ))}
      </div>

      <div className="row" style={{ margin: "4px 0 16px" }}>
        {siteOpts.length > 1 && <div className="field" style={{ flex: "0 1 240px" }}><label htmlFor="ls">Cơ sở</label>
          <select id="ls" value={site} onChange={(e) => setSite(e.target.value)}><option value="">Tất cả</option>
            {siteOpts.map((k) => <option key={k} value={k}>{SITES[k]}</option>)}</select></div>}
        {/* Q11: lọc theo loại (Pháp lý, Hợp đồng, Tài sản & Bảo trì…) */}
        {catOpts.length > 1 && <div className="field" style={{ flex: "0 1 240px" }}><label htmlFor="lc">Loại</label>
          <select id="lc" value={cat} onChange={(e) => setCat(e.target.value)}><option value="">Tất cả</option>
            {catOpts.map((k) => <option key={k} value={k}>{DOC_CATEGORIES[k]}</option>)}</select></div>}
      </div>
      {err && <div className="err" style={{ marginBottom: 12 }}>{err}</div>}
      {!err && items.length === 0 && <div className="card">Chưa có tài liệu nào có ngày hiệu lực. Khi tải lên hợp đồng, giấy phép, bảo hiểm, hãy điền ô "Hiệu lực đến".</div>}

      {GROUPS.map((g) => {
        const list = items.filter((i) => g.test(i.daysLeft));
        if (!list.length) return null;
        return (
          <section key={g.key} style={{ marginBottom: 20 }}>
            <h2 className={`grpH ${g.cls}`}>{g.label} <span>· {list.length}</span></h2>
            <div className="card tableWrap" style={{ padding: 0 }}>
              <table className="table stack">
                <thead><tr><th>Mã</th><th>Tài liệu</th><th>Cơ sở</th><th>Hết hạn</th><th>Gia hạn</th><th></th></tr></thead>
                <tbody>
                  {list.map((r) => {
                    const manage = canManageExpiry(member, r);
                    const isEdit = editing?.id === r.id;
                    return (
                      <tr key={r.id}>
                        <td className="code cardHead">{r.code}</td>
                        <td>{r.title}<div className="muted">{DOC_CATEGORIES[r.category]} · phụ trách {r.ownerName}</div></td>
                        <td data-label="Cơ sở">{SITES[r.siteId]}</td>
                        <td data-label="Hết hạn"><b>{fmt(r.expiresAt)}</b><div className={`left ${g.cls}`}>{r.daysLeft <= 0 ? `quá ${-r.daysLeft} ngày` : `còn ${r.daysLeft} ngày`}</div></td>
                        <td data-label="Gia hạn">{r.renewal?.state === "in_progress"
                          ? <><span className="pill pending_review">Đang gia hạn</span><div className="muted">{r.renewal.by}{r.renewal.note ? `: ${r.renewal.note}` : ""}</div></>
                          : r.pendingVersion ? <span className="pill pending_review">Bản mới chờ duyệt</span> : <span className="muted">—</span>}</td>
                        <td className="actions">
                          {isEdit ? (
                            <div className="row" style={{ gap: 6 }}>
                              {editing.mode === "date"
                                ? <input type="date" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} style={{ minHeight: 44, borderRadius: 10, border: "1px solid #D5DAD2", padding: "0 10px" }} />
                                : <input placeholder="Ghi chú (vd: đã nộp hồ sơ)" value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} style={{ minHeight: 44, borderRadius: 10, border: "1px solid #D5DAD2", padding: "0 10px", flex: 1 }} />}
                              <button className="btn primary" disabled={busy} onClick={save}>Lưu</button>
                              <button className="btn" onClick={() => setEditing(null)}>Hủy</button>
                            </div>
                          ) : (
                            <div className="row-actions">
                              <button className="btn" onClick={() => nav(`/xem/${r.id}`)}>Mở</button>
                              {manage && !r.pendingVersion && canUploadNewVersion(member, r) &&
                                <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to={`/documents/upload?replace=${r.id}`}>Tải bản gia hạn</Link>}
                              {manage && (r.renewal?.state === "in_progress"
                                ? <button className="btn" onClick={() => clearRenewal(r.id)}>Bỏ đánh dấu</button>
                                : <button className="btn" onClick={() => setEditing({ id: r.id, mode: "renew", value: "" })}>Đang gia hạn</button>)}
                              {manage && <button className="btn" onClick={() => setEditing({ id: r.id, mode: "date", value: toInput(r.expiresAt) })}>Sửa ngày</button>}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
      {/* L56: chỉ hướng dẫn gia hạn cho người có nút gia hạn */}
      {items.some((r) => canManageExpiry(member, r)) && <p className="muted">Gia hạn: bấm "Tải bản gia hạn", chọn file hợp đồng/giấy phép mới và điền ngày "Hiệu lực đến" mới. Khi bản mới được duyệt, ngày hết hạn tự cập nhật.</p>}
    </>
  );
}
